import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlayCircle, ShieldCheck, Building2, CheckCircle2 } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { canManageSop } from "@/lib/permissions";
import { startTour } from "@/lib/tour";
import api from "@/lib/api";

const GUIDES = {
  national_admin: {
    icon: ShieldCheck,
    label: "Administrateur National",
    tourKey: "national_admin",
    tourPath: "/dashboard",
    steps: [
      "Consultez les statistiques (histogramme et tableau croisé) sur le tableau de bord pour suivre l'activité de tous les sites.",
      "Utilisez la recherche et les filtres (site, service, année) pour retrouver n'importe quelle SOP.",
      "Créez une SOP pour n'importe quel site via le bouton « Nouvelle SOP ».",
      "Ouvrez « Utilisateurs » pour gérer les rôles (Créateur, Valideur, Admin Site), ajouter/supprimer des sites et gérer les Administrateurs Nationaux.",
      "Consultez l'historique des modifications de rôles depuis le bouton « Historique » de cette même page.",
    ],
  },
  site_admin: {
    icon: Building2,
    label: "Administrateur Site",
    tourKey: "site_admin",
    tourPath: "/users",
    steps: [
      "Ouvrez « Utilisateurs » dans le menu : vous ne voyez et gérez que les sites dont vous êtes administrateur.",
      "Ajoutez/retirez des emails dans les listes Créateurs, Valideurs et Utilisateurs de vos sites.",
      "Ajoutez un nouveau service pour l'un de vos sites via « Ajouter un service » (le site est limité à ceux que vous gérez).",
      "Vous ne pouvez pas modifier la liste des Administrateurs Site ni supprimer un site - seul l'Administrateur National le peut.",
    ],
  },
  validator: {
    icon: CheckCircle2,
    label: "Valideur",
    tourKey: "validator",
    tourPath: null,
    steps: [
      "Ouvrez la SOP à valider (depuis le tableau de bord, bouton « Modifier »).",
      "Relisez l'onglet « Informations » (titre, site, créateur), puis parcourez les onglets « Étapes » et « EPI » pour vérifier le contenu complet.",
      "Une fois la SOP correcte, indiquez votre nom dans le champ « Validateur » de l'onglet Informations.",
      "Allez dans l'onglet « Récapitulatif » et cliquez sur « Enregistrer » : la SOP porte désormais votre nom comme preuve de validation.",
    ],
  },
};

export default function Help() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const defaultTab = GUIDES[user?.permissions?.role] ? user.permissions.role : "national_admin";

  const handleStartTour = async (guide) => {
    if (guide.tourPath) {
      startTour(guide.tourKey);
      navigate(guide.tourPath);
      return;
    }
    // Validator tour: find a real SOP the current user can edit for the demo.
    try {
      const res = await api.get("/sops");
      const editable = res.data.find((sop) => canManageSop(user, sop));
      if (!editable) {
        toast.error("Aucune SOP à valider n'a été trouvée sur vos sites pour lancer la démonstration.");
        return;
      }
      startTour(guide.tourKey);
      navigate(`/sops/${editable.id}/edit`);
    } catch (e) {
      toast.error("Impossible de charger une SOP de démonstration.");
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="help-page">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-semibold text-foreground">Aide & Tutoriel</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comment utiliser l'outil selon votre rôle, avec une visite guidée interactive.
          </p>
        </div>

        <Tabs defaultValue={defaultTab} data-testid="help-tabs">
          <TabsList className="grid grid-cols-3 w-full mb-6">
            {Object.entries(GUIDES).map(([key, guide]) => (
              <TabsTrigger key={key} value={key} data-testid={`help-tab-${key}`}>
                {guide.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(GUIDES).map(([key, guide]) => {
            const Icon = guide.icon;
            return (
              <TabsContent key={key} value={key} className="space-y-4">
                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon className="h-5 w-5 text-primary" />
                    <h2 className="font-heading text-lg font-semibold text-foreground">{guide.label}</h2>
                  </div>
                  <ol className="space-y-2.5 mb-5">
                    {guide.steps.map((step, i) => (
                      <li key={i} className="flex gap-3 text-sm text-foreground" data-testid={`help-step-${key}-${i}`}>
                        <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary/10 text-primary font-semibold text-xs flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                  <Button onClick={() => handleStartTour(guide)} data-testid={`start-tour-button-${key}`}>
                    <PlayCircle className="h-4 w-4" />
                    Lancer la visite guidée
                  </Button>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </Layout>
  );
}
